import React, { useEffect, useMemo, useCallback } from 'react';
import { applyFilters } from '../utils/filterUtils';
import {
  getValidOptionsSet,
  getGroupCounts,
  getLocationCounts,
  getAllGroupNames,
  getAllVenues
} from '../utils/filterOptionUtils';
import { useVenueSearch } from '../hooks/useVenueSearch';
import { getDateRangeFromRows } from '../utils/dateUtils';
import { getMonthDateGrids } from '../utils/calendarUtils';
import { toggleFilterValue } from '../utils/filterHelpers';
import { useGroupExpansion } from '../hooks/useGroupExpansion';
import { ALL_DIFFICULTIES, ALL_AVAILABILITIES } from '../constants/filterOptions';
import { useShowAllGroups } from '../hooks/useShowAllGroups';
import VenueTagList from './VenueTagList';
import VenueSuggestionsDropdown from './VenueSuggestionsDropdown';
import GroupFilter from './GroupFilter';
import DateMonthGrid from './DateMonthGrid';

const FilterSection = ({ rows, filters, setFilters, setFilteredRows }) => {
  // Remove the state for toggling filters open/close

  // Compute the full date range present in the data (used for date filter UI)
  const dateRange = useMemo(() => getDateRangeFromRows(rows), [rows]);

  // Show/hide all groups in the group filter (see useShowAllGroups hook)
  const { showAllGroups, toggleShowAllGroups } = useShowAllGroups(false);

  // Track which groups are expanded to show their venues (see useGroupExpansion hook)
  const { expandedGroups, toggleGroupExpansion } = useGroupExpansion();

  // Get all unique venues and group names from the data
  const allVenues = useMemo(() => getAllVenues(rows), [rows]);
  const allGroupNames = useMemo(() => getAllGroupNames(rows), [rows]);

  // Venue search logic (see useVenueSearch hook)
  const {
    venueSearch,
    selectedVenues,
    showSuggestions,
    setShowSuggestions,
    searchContainerRef,
    venueSuggestions,
    handleSearchChange,
    handleSuggestionClick,
    handleRemoveSelectedVenue,
  } = useVenueSearch(allVenues, filters, setFilters);  // Pass filters and setFilters

  // Filter rows by selected venues only (used for filter validity/counts)
  const rowsFilteredByVenueSearch = useMemo(() => {
    if (selectedVenues.length === 0) {
      return rows;
    }
    return rows.filter(row => selectedVenues.includes(row.venue));
  }, [rows, selectedVenues]);

  // Update filtered rows in parent whenever filters or venue search changes
  useEffect(() => {
    const venueFiltered = selectedVenues.length > 0
      ? rows.filter(row => selectedVenues.includes(row.venue))
      : rows;
    setFilteredRows(applyFilters(venueFiltered, filters));
  }, [rows, filters, selectedVenues, setFilteredRows]);

  // Compute which filter options are valid given current selections
  const getValidOptionsSetCb = useCallback(
    (filterType) => getValidOptionsSet(filterType, rowsFilteredByVenueSearch, filters),
    [rowsFilteredByVenueSearch, filters]
  );
  const validGroupNames = useMemo(() => getValidOptionsSetCb('group'), [getValidOptionsSetCb]);
  const validLocations = useMemo(() => getValidOptionsSetCb('location'), [getValidOptionsSetCb]);
  const validAvailabilities = useMemo(() => getValidOptionsSetCb('availability'), [getValidOptionsSetCb]);
  const validDifficulties = useMemo(() => getValidOptionsSetCb('difficulty'), [getValidOptionsSetCb]);
  const validDaysOfWeek = useMemo(() => getValidOptionsSetCb('dayOfWeek'), [getValidOptionsSetCb]);
  const validDates = useMemo(() => getValidOptionsSetCb('dates'), [getValidOptionsSetCb]);

  // Count events per group and per venue for display
  const groupCounts = useMemo(
    () => getGroupCounts(rowsFilteredByVenueSearch, filters),
    [rowsFilteredByVenueSearch, filters]
  );
  const locationCounts = useMemo(
    () => getLocationCounts(rowsFilteredByVenueSearch, filters),
    [rowsFilteredByVenueSearch, filters]
  );

  // Toggle a filter value (checkboxes)
  const handleFilterChange = useCallback((filterType, value) => {
    setFilters(prevFilters => toggleFilterValue(prevFilters, filterType, value));
  }, [setFilters]);

  // --- Rendering ---
  return (
    <div className="p-2 pt-1 border border-gray-300 rounded-lg bg-white bg-opacity-90 backdrop-blur-sm">
      {/* Reduce vertical spacing between major filter sections */}
      <div className="space-y-2">
        {/* Venue Search Section */}
        <div ref={searchContainerRef} className="relative">
          <h3 className="font-semibold mb-1 text-gray-700">Venue Search</h3>
          <div className="relative flex items-center">
            <input
              type="text"
              placeholder="Search and select venues..."
              value={venueSearch}
              onChange={handleSearchChange}
              onFocus={() => venueSearch.length > 0 && setShowSuggestions(true)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
            />
          </div>
          {/* Show selected venues as tags */}
          {selectedVenues.length > 0 && (
            <VenueTagList venues={selectedVenues} onRemove={handleRemoveSelectedVenue} />
          )}
          {/* Show dropdown of venue suggestions */}
          {showSuggestions && venueSuggestions.length > 0 && (
            <VenueSuggestionsDropdown
              suggestions={venueSuggestions}
              onSelect={handleSuggestionClick}
            />
          )}
        </div>

        {/* Group Filter */}
        <GroupFilter
          allGroupNames={allGroupNames}
          showAllGroups={showAllGroups}
          toggleShowAllGroups={toggleShowAllGroups}
          validGroupNames={validGroupNames}
          groupCounts={groupCounts}
          expandedGroups={expandedGroups}
          toggleGroupExpansion={toggleGroupExpansion}
          filters={filters}
          handleFilterChange={handleFilterChange}
          rows={rows}
          validLocations={validLocations}
          locationCounts={locationCounts}
        />

        {/* Availability & Difficulty Filters */}
        <div className="flex gap-4">
          {/* Availability Filter: vertical column */}
          <div className="flex flex-col items-start">
            <h3 className="font-semibold mb-1 text-gray-700">Availability</h3>
            <div className="flex flex-col gap-1">
              {ALL_AVAILABILITIES.map((availability) => {
                const isSelected = (filters.availability || []).includes(availability);
                const isValid = validAvailabilities.has(availability);
                return (
                  <label
                    key={availability}
                    className={`px-2 py-0.5 border rounded cursor-pointer text-xs ${
                      isSelected
                        ? 'bg-blue-500 text-white'
                        : isValid
                        ? 'bg-gray-100 hover:bg-gray-200'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleFilterChange('availability', availability)}
                      className="hidden"
                      disabled={!isValid && !isSelected}
                    />
                    {availability}
                  </label>
                );
              })}
            </div>
          </div>
          {/* Difficulty Filter: two columns */}
          <div className="flex-1">
            <h3 className="font-semibold mb-1 text-gray-700">Difficulty</h3>
            <div className="grid grid-cols-2 gap-1">
              {ALL_DIFFICULTIES.map((difficulty) => {
                const isSelected = (filters.difficulty || []).includes(difficulty);
                const isValid = validDifficulties.has(difficulty);
                return (
                  <label
                    key={difficulty}
                    className={`px-2 py-0.5 border rounded cursor-pointer text-xs ${
                      isSelected
                        ? 'bg-blue-500 text-white'
                        : isValid
                        ? 'bg-gray-100 hover:bg-gray-200'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleFilterChange('difficulty', difficulty)}
                      className="hidden"
                      disabled={!isValid && !isSelected}
                    />
                    {difficulty}
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {/* Date and Day of Week Filter */}
        <div>
          <h3 className="font-semibold mb-1 text-gray-700">Date</h3>
          <DateMonthGrid
            dateRange={dateRange}
            filters={filters}
            validDaysOfWeek={validDaysOfWeek}
            validDates={validDates}
            handleFilterChange={handleFilterChange}
            getMonthDateGrids={getMonthDateGrids}
          />
        </div>
      </div>
    </div>
  );
};

export default FilterSection;
